import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Layout } from '../components/Layout';
import { ChartBar, CheckCircle, XCircle, CurrencyDollar, CreditCard, Money, EnvelopeSimple } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_NAMES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const fmtMonth = (m) => { if (!m) return ''; const [y, mm] = m.split('-'); return `${MONTH_NAMES[parseInt(mm, 10) - 1]} / ${y}`; };
const money = (v) => `$${Number(v || 0).toFixed(2)}`;

const METHOD_STYLE = {
  'Cartão': 'bg-blue-100 text-blue-700',
  'Zelle': 'bg-purple-100 text-purple-700',
  'Pix': 'bg-teal-100 text-teal-700',
  'Dinheiro': 'bg-green-100 text-green-700',
  'Outro': 'bg-gray-100 text-gray-600',
};

function StatCard({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="bg-white border border-[#E6E2D8] rounded-xl p-4 flex items-center gap-3" data-testid={testid}>
      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}1A` }}>
        <Icon size={24} weight="fill" style={{ color }} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-[#73706A] uppercase tracking-wide truncate">{label}</p>
        <p className="text-xl font-['Outfit'] font-bold text-[#1A1A1A]">{value}</p>
      </div>
    </div>
  );
}

export default function Reports() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const sendReminders = async () => {
    if (!window.confirm(`Enviar lembrete de pagamento por e-mail a ${data.summary.count_unpaid} inadimplente(s) de ${fmtMonth(month)}?`)) return;
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const { data: res } = await axios.post(`${API}/admin/reports/send-reminders`, { month }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${res.sent} lembrete(s) enviado(s)${res.errors ? ` · ${res.errors} sem e-mail` : ''}`);
    } catch { toast.error('Erro ao enviar lembretes'); } finally { setSending(false); }
  };

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/admin/reports/payments${m ? `?month=${m}` : ''}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(data);
      setMonth(data.month);
    } catch { toast.error('Erro ao carregar relatório'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const s = data?.summary;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-6" data-testid="reports-page">
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3">
            <ChartBar size={28} className="text-[#E85D04]" weight="fill" />
            <h1 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A]">Relatórios de Pagamento</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={month}
              onChange={(e) => load(e.target.value)}
              className="border border-[#E6E2D8] rounded-lg px-3 py-2 text-sm font-semibold bg-white"
              data-testid="report-month-select"
            >
              {(data?.available_months || []).map(m => <option key={m} value={m}>{fmtMonth(m)}</option>)}
            </select>
            <button onClick={sendReminders} disabled={sending || !s || s.count_unpaid === 0} className="inline-flex items-center gap-2 bg-[#9B2226] text-white px-4 py-2 rounded-lg font-semibold text-sm hover:bg-[#7a1a1d] disabled:opacity-50" data-testid="send-reminders-btn">
              <EnvelopeSimple size={16} weight="fill" /> {sending ? 'Enviando...' : 'Cobrar Inadimplentes'}
            </button>
          </div>
        </div>

        {loading ? <p className="text-center text-[#73706A] py-10">Carregando...</p> : data && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              <StatCard icon={CurrencyDollar} label="Total Recebido" value={money(s.total_paid)} color="#2D6A4F" testid="stat-total-paid" />
              <StatCard icon={CheckCircle} label="Alunos Pagos" value={s.count_paid} color="#E85D04" testid="stat-count-paid" />
              <StatCard icon={XCircle} label="Não Pagaram" value={s.count_unpaid} color="#9B2226" testid="stat-count-unpaid" />
              <StatCard icon={Money} label="A Receber" value={money(s.total_pending)} color="#0077B6" testid="stat-total-pending" />
            </div>

            {Object.keys(s.by_method).length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6" data-testid="method-breakdown">
                {Object.entries(s.by_method).map(([m, v]) => (
                  <span key={m} className={`text-sm font-semibold px-3 py-1.5 rounded-lg ${METHOD_STYLE[m] || METHOD_STYLE['Outro']}`}>
                    {m === 'Cartão' ? <CreditCard size={15} className="inline mr-1" weight="fill" /> : null}{m}: {money(v)}
                  </span>
                ))}
              </div>
            )}

            {/* Pagos */}
            <section className="mb-8">
              <h2 className="font-['Outfit'] font-bold text-[#2D6A4F] mb-2 flex items-center gap-2">
                <CheckCircle size={20} weight="fill" /> Pagos — {fmtMonth(month)} ({s.count_paid})
              </h2>
              <div className="bg-white border border-[#E6E2D8] rounded-xl overflow-hidden">
                {data.paid.length === 0 ? (
                  <p className="p-5 text-sm text-[#73706A] text-center" data-testid="paid-empty">Nenhum pagamento registrado neste mês.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#FDFBF7] text-[#73706A] text-xs uppercase">
                        <th className="text-left px-4 py-2">Aluno</th>
                        <th className="text-left px-4 py-2 hidden sm:table-cell">Plano</th>
                        <th className="text-right px-4 py-2">Valor</th>
                        <th className="text-center px-4 py-2">Forma</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.paid.map((p, i) => (
                        <tr key={i} className="border-t border-[#F0EDE5]" data-testid={`paid-row-${i}`}>
                          <td className="px-4 py-2.5 font-medium text-[#1A1A1A]">{p.student_name}</td>
                          <td className="px-4 py-2.5 text-[#73706A] hidden sm:table-cell">{p.plan || '-'}</td>
                          <td className="px-4 py-2.5 text-right font-bold text-[#2D6A4F]" data-testid={`paid-amount-${i}`}>{money(p.amount)}</td>
                          <td className="px-4 py-2.5 text-center">
                            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${METHOD_STYLE[p.payment_method] || METHOD_STYLE['Outro']}`} data-testid={`paid-method-${i}`}>{p.payment_method}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </section>

            {/* Não Pagos */}
            <section>
              <h2 className="font-['Outfit'] font-bold text-[#9B2226] mb-2 flex items-center gap-2">
                <XCircle size={20} weight="fill" /> Não Pagaram — {fmtMonth(month)} ({s.count_unpaid})
              </h2>
              <div className="bg-white border border-[#E6E2D8] rounded-xl overflow-hidden">
                {data.unpaid.length === 0 ? (
                  <p className="p-5 text-sm text-[#73706A] text-center" data-testid="unpaid-empty">Todos pagaram este mês! 🎉</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#FDFBF7] text-[#73706A] text-xs uppercase">
                        <th className="text-left px-4 py-2">Aluno</th>
                        <th className="text-left px-4 py-2 hidden sm:table-cell">Plano</th>
                        <th className="text-right px-4 py-2">Valor devido</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.unpaid.map((p, i) => (
                        <tr key={i} className="border-t border-[#F0EDE5]" data-testid={`unpaid-row-${i}`}>
                          <td className="px-4 py-2.5 font-medium text-[#1A1A1A]">{p.student_name}</td>
                          <td className="px-4 py-2.5 text-[#73706A] hidden sm:table-cell">{p.plan || '-'}</td>
                          <td className="px-4 py-2.5 text-right font-bold text-[#9B2226]">{money(p.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </section>
          </>
        )}
      </div>
    </Layout>
  );
}
